'use client'
import {
  useGetOtcPrice,
  useListProjects,
  useOtcPayOrder,
  useOtcPlaceOrder,
  useOtcPlaceConvertOrder,
  useOtcFinalizeConvertOrder,
  useUserAuthDetails,
  useUserPortfolio,
} from '@repo/services'
import {
  OrderBox,
  OrderBoxTab,
  mapOrderDataToFlat,
  mapProjectToFlatAsset,
  type FlatAsset,
} from '@repo/shared/components'
import type { ProjectsProject } from '@repo/shared/types'
import { Col, toast } from '@repo/ui/components'
import { useTranslate } from '@repo/shared/hooks'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ProjectsDetailOrderBoxEmptyState } from './projects-detail-order-box.empty-state'
import { Cookie } from '@repo/tools'

export const ProjectDetailsSidebar = ({
  project,
}: {
  project: ProjectsProject
}) => {
  const [selectedAsset, setSelectedAsset] = React.useState<
    FlatAsset | undefined
  >(mapProjectToFlatAsset(project))

  console.log('selectedAsset', selectedAsset)

  const [tab, setTab] = useState<OrderBoxTab>('buy')
  const [searchQuery, setSearchQuery] = useState('')
  const [fromAsset, setFromAsset] = useState<FlatAsset | undefined>(undefined)
  const [toAsset, setToAsset] = useState<FlatAsset | undefined>(undefined)
  const [convertOrderId, setConvertOrderId] = useState<string | undefined>()
  const [convertOrderBaseAmount, setConvertOrderBaseAmount] = useState<
    number | undefined
  >(undefined)
  const [convertOrderTargetAmount, setConvertOrderTargetAmount] = useState<
    number | undefined
  >(undefined)
  const [fromAmount, setFromAmount] = useState(0)
  const [toAmount, setToAmount] = useState(0)

  // Flag to prevent infinite loops during API calculations
  const isCalculatingRef = useRef(false)

  // API hooks
  const { data: user } = useUserAuthDetails()
  const { t } = useTranslate()
  const { data: { data = {} } = {}, isLoading: isPortfolioLoading } =
    useUserPortfolio()

  // List projects hook for asset select
  const listProjectsData = useListProjects({
    skip: 0,
    limit: 10,
    kind: 'crypto',
    tradeStatus: 'active',
    // provinceID: 123,
    ...(searchQuery && {
      q: searchQuery,
    }),
  })
  const {
    data: otcPrice,
    isLoading: isOtcPriceLoading,
    isFetching,
    refetch: refetchOtcPrice,
  } = useGetOtcPrice({
    symbol: selectedAsset?.token || '',
    base: 'IRR',
    orderType: tab === 'convert' ? 'sell' : tab,
  })

  // Get prices for convert mode
  const { data: fromAssetPrice, isLoading: isFromAssetPriceLoading } =
    useGetOtcPrice({
      symbol: fromAsset?.token || '',
      base: 'IRR',
      orderType: 'sell',
    })

  const { data: toAssetPrice, isLoading: isToAssetPriceLoading } =
    useGetOtcPrice({
      symbol: toAsset?.token || '',
      base: 'IRR',
      orderType: 'buy',
    })

  // Calculate conversion rate
  const conversionRate = useMemo(() => {
    if (
      !fromAssetPrice?.data?.price ||
      !toAssetPrice?.data?.price ||
      toAssetPrice.data.price === 0
    ) {
      return 0
    }
    // Conversion rate = fromAsset sell price / toAsset buy price
    return fromAssetPrice.data.price / toAssetPrice.data.price
  }, [fromAssetPrice?.data?.price, toAssetPrice?.data?.price])

  const isConversionRateLoading =
    isFromAssetPriceLoading || isToAssetPriceLoading

  const {
    mutate: otcPlaceOrder,
    data: otcPlaceOrderData,
    isPending: isOtcPlaceOrderLoading,
    reset: resetOtcPlaceOrder,
  } = useOtcPlaceOrder({
    invalidate: true,
  })

  const {
    mutate: otcPayOrder,
    isPending: isOtcPayOrderLoading,
    isSuccess,
  } = useOtcPayOrder({
    invalidate: true,
  })

  useEffect(() => {
    if (isSuccess) {
      setTimeout(() => {
        refetchOtcPrice()
      }, 1000)
    }
  }, [isSuccess])

  const {
    mutate: otcPlaceConvertOrder,
    data: otcPlaceConvertOrderData,
    isPending: isOtcPlaceConvertOrderLoading,
    reset: resetOtcPlaceConvertOrder,
  } = useOtcPlaceConvertOrder({
    invalidate: true,
  })

  const {
    mutate: calculateConvertAmount,
    isPending: isCalculatingConvertAmount,
  } = useOtcPlaceConvertOrder({
    invalidate: false, // Don't invalidate queries for calculation
  })

  // Hook for checking referral code in convert dialog
  const { mutate: checkReferralCodeConvert } = useOtcPlaceConvertOrder({
    invalidate: false, // Don't invalidate queries for referral check
  })

  const {
    mutate: otcFinalizeConvertOrder,
    isPending: isOtcFinalizeConvertOrderLoading,
  } = useOtcFinalizeConvertOrder({
    invalidate: true,
  })

  // Reset function for OTC data
  const handleResetOtcData = () => {
    // Reset the OTC place order mutation data
    resetOtcPlaceOrder()
    resetOtcPlaceConvertOrder()
    setConvertOrderId(undefined)
    setConvertOrderBaseAmount(undefined)
    setConvertOrderTargetAmount(undefined)
  }

  // Handle calculating received amount using API
  const handleCalculateReceivedAmount = useCallback(
    (fromAmount: number, revert?: boolean) => {
      if (!fromAsset || !toAsset || fromAmount <= 0) {
        return
      }
      const targetScale = fromAsset.scale || 1
      const apiAmount = Number((fromAmount * targetScale).toFixed(0))

      isCalculatingRef.current = true
      calculateConvertAmount(
        {
          submit: false, // Calculate only, don't submit order
          fromCurrency: revert ? toAsset.token : fromAsset.token,
          toCurrency: revert ? fromAsset.token : toAsset.token,
          amount: apiAmount,
        },
        {
          onSuccess: (response) => {
            // targetAmount from buyOrder is the calculated received amount
            const calculatedToAmount =
              response?.data?.order?.buyOrder?.targetAmount

            if (calculatedToAmount !== undefined && calculatedToAmount > 0) {
              const targetScale = otcPrice?.data?.targetScale || 1
              const adjustedToAmount = calculatedToAmount / targetScale

              setToAmount(adjustedToAmount)
            }
            isCalculatingRef.current = false
          },
          onError: (error) => {
            console.error('Calculate convert amount error:', error)
            // Fallback to manual calculation if API fails
            if (conversionRate && conversionRate > 0) {
              const calculatedToAmount = fromAmount * conversionRate
              setToAmount(calculatedToAmount)
            }
            isCalculatingRef.current = false
          },
        },
      )
    },
    [
      fromAsset,
      toAsset,
      calculateConvertAmount,
      conversionRate,
      otcPrice?.data?.targetScale,
    ],
  )

  // Handle calculating received amount when user changes toAmount (reverse calculation)
  const handleCalculateReceivedAmountForToAmount = useCallback(
    (toAmount: number) => {
      if (!fromAsset || !toAsset || toAmount <= 0) {
        return
      }
      const targetScale = toAsset.scale || 1
      const apiAmount = Number((toAmount * targetScale).toFixed(0))

      isCalculatingRef.current = true
      calculateConvertAmount(
        {
          submit: false, // Calculate only, don't submit order
          fromCurrency: toAsset.token, // Reversed: from toAsset
          toCurrency: fromAsset.token, // Reversed: to fromAsset
          amount: apiAmount,
        },
        {
          onSuccess: (response) => {
            // targetAmount from buyOrder is the calculated amount for fromAsset
            const calculatedFromAmount =
              response?.data?.order?.buyOrder?.targetAmount

            if (
              calculatedFromAmount !== undefined &&
              calculatedFromAmount > 0
            ) {
              const targetScale = otcPrice?.data?.targetScale || 1
              const adjustedFromAmount = calculatedFromAmount / targetScale
              setFromAmount(adjustedFromAmount)
            }
            isCalculatingRef.current = false
          },
          onError: (error) => {
            console.error('Calculate convert amount for toAmount error:', error)
            // Fallback to manual calculation if API fails
            if (conversionRate && conversionRate > 0) {
              const calculatedFromAmount = toAmount / conversionRate
              setFromAmount(calculatedFromAmount)
            }
            isCalculatingRef.current = false
          },
        },
      )
    },
    [
      fromAsset,
      toAsset,
      calculateConvertAmount,
      conversionRate,
      otcPrice?.data?.targetScale,
    ],
  )

  // Handle convert order submission
  const handleConvertSubmit = (
    submitFromAmount?: number,
    referralCode?: string,
  ) => {
    // Use the submitted amount if provided, otherwise fall back to state
    const amountToUse = submitFromAmount ?? fromAmount
    if (!fromAsset || !toAsset || amountToUse <= 0) return

    const targetScale = fromAsset.scale || 1

    // Get referral code (exclude MALEK token and empty codes)
    const finalReferralCode =
      fromAsset?.token === 'MALEK' ||
      !referralCode ||
      referralCode === 'non-default'
        ? ''
        : referralCode

    otcPlaceConvertOrder(
      {
        submit: true,
        fromCurrency: fromAsset.token,
        toCurrency: toAsset.token,
        amount: Number((amountToUse * targetScale).toFixed(0)),
        ...(finalReferralCode && { referralCode: finalReferralCode }),
      },
      {
        onSuccess: (response) => {
          // Response structure: { data: { order: { ID: string, buyOrder: { baseAmount: number, targetAmount: number }, sellOrder: { baseAmount: number, targetAmount: number } }, expireAt: string } }
          const orderId = response?.data?.order?.ID
          // baseAmount from buyOrder is the amount of toAsset that will be received
          const baseAmount = response?.data?.order?.buyOrder?.baseAmount
          // targetAmount from buyOrder is the final amount of toAsset after conversion
          const targetAmount = response?.data?.order?.buyOrder?.targetAmount
          if (orderId) {
            setConvertOrderId(orderId)
            // Store baseAmount and targetAmount in state to pass to dialog
            if (baseAmount !== undefined) {
              setConvertOrderBaseAmount(baseAmount)
            }
            if (targetAmount !== undefined) {
              setConvertOrderTargetAmount(targetAmount)
            }
          }
        },
        onError: (error) => {
          console.error('Convert order error:', error)
        },
      },
    )
  }

  // Handle refresh price for convert (calls API with submit: false)
  const handleRefreshConvertPrice = (
    fromAmountParam: number,
    referralCodeParam?: string,
    onSuccess?: () => void,
  ) => {
    // Use the provided amount or fall back to state
    const amountToUse = fromAmountParam || fromAmount
    if (!fromAsset || !toAsset || amountToUse <= 0) return

    const targetScale = fromAsset.scale || 1

    // Get referral code (exclude MALEK token and empty codes)
    const finalReferralCode =
      fromAsset?.token === 'MALEK' ||
      !referralCodeParam ||
      referralCodeParam === 'non-default'
        ? ''
        : referralCodeParam

    // Call API with submit: false to refresh price only
    otcPlaceConvertOrder(
      {
        submit: false, // Only refresh price, don't submit order
        fromCurrency: fromAsset.token,
        toCurrency: toAsset.token,
        amount: Number((amountToUse * targetScale).toFixed(0)),
        ...(finalReferralCode && { referralCode: finalReferralCode }),
      },
      {
        onSuccess: (response) => {
          console.log('Convert price refreshed:', response)
          // Call the success callback to update isPriceValid in OrderBox
          onSuccess?.()
        },
        onError: (error) => {
          console.error('Refresh convert price error:', error)
        },
      },
    )
  }

  // Handle referral code check in convert dialog
  const handleCheckReferralCode = (
    referralCode: string,
    fromAmountParam: number,
    onSuccess: (data: { code: string }) => void,
    onError: (error: any) => void,
  ) => {
    // Use fromAmountParam (from form) instead of state fromAmount
    const amountToUse = fromAmountParam || fromAmount

    if (!fromAsset || !toAsset || amountToUse <= 0) {
      onError(new Error('Invalid convert parameters'))
      return
    }

    const targetScale = fromAsset.scale || 1

    console.log('handleCheckReferralCode called with:', {
      referralCode,
      fromAsset: fromAsset.token,
      toAsset: toAsset.token,
      fromAmount: amountToUse,
      targetScale,
    })

    checkReferralCodeConvert(
      {
        submit: false, // Only check, don't submit
        fromCurrency: fromAsset.token,
        toCurrency: toAsset.token,
        amount: Number((amountToUse * targetScale).toFixed(0)),
        // referralCode: referralCode,
      },
      {
        onSuccess: (response) => {
          // Extract referral data from response
          const referralData = {
            code: referralCode,
          }
          onSuccess(referralData)
        },
        onError: (error) => {
          console.error('Check referral code error:', error)
          onError(error)
        },
      },
    )
  }

  // Handle convert order finalization
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleConvertFinalize = (_referralCode?: string) => {
    if (!convertOrderId) return

    // Note: referralCode is already included in otcPlaceConvertOrder
    // This parameter is kept for future use if needed
    otcFinalizeConvertOrder(
      {
        orderID: convertOrderId,
      },
      {
        onSuccess: () => {
          toast.success({
            title: t('order_box_convert_success', {
              fromAmount: (
                fromAmount / (fromAsset?.scale || 1)
              ).toLocaleString(),
              fromAsset: fromAsset?.token || '',
              toAmount: (toAmount / (toAsset?.scale || 1)).toLocaleString(),
              toAsset: toAsset?.token || '',
            }),
          })
          setFromAmount(0)
          setToAmount(0)
          setConvertOrderId(undefined)
          setConvertOrderTargetAmount(undefined)
          resetOtcPlaceConvertOrder()
        },
        onError: (error) => {
          console.error('Finalize convert order error:', error)
        },
      },
    )
  }

  // Handle from amount change and trigger API calculation
  const handleFromAmountChange = useCallback((amount: number) => {
    setFromAmount(amount)
  }, [])

  // Process user portfolio data
  const accounts = data?.accounts
  const projects = data?.projects

  // Get user's assets for sell mode with project data
  const userAssets = useMemo(() => {
    const accounts =
      data?.accounts?.filter(
        (account) =>
          account.type === 'asset' || account.currency === project?.token,
      ) || []

    const mappedAccounts = accounts.map((account) => {
      // Find matching project data from projects array
      const matchingProject = projects?.find(
        (p) =>
          p.token === account.currency || p.id?.toString() === account.currency,
      )

      return {
        ...account,
        // Add project data if found
        displayName: matchingProject?.displayName || account.currency || '',
        logoURL: matchingProject?.logoURL || '',
        description: matchingProject?.description || '',
        location: matchingProject?.location || {},
        state: matchingProject?.state || '',
        tradeStatus: matchingProject?.tradeStatus || 'active',
        // Keep original account type for sorting
        accountType: account.type,
        type: matchingProject?.type || 'project',
        // Ensure token is set for API calls
        token: matchingProject?.token || account.currency || '',
        kind: matchingProject?.kind || '',
        scale: matchingProject?.scale || 1,
      }
    })

    // Filter out accounts with zero availableBalance and sort
    return mappedAccounts
      .filter((account) => (account.availableBalance ?? 0) > 0)
      .map((account) => mapProjectToFlatAsset(account))
      .sort((a, b) => {
        if (a.accountType === 'asset' && b.accountType !== 'asset') return -1
        if (a.accountType !== 'asset' && b.accountType === 'asset') return 1
        return 0
      })
  }, [data?.accounts, projects, project])

  const accountAvailableBalance = useMemo(() => {
    return accounts?.find(
      (account) => account.type === 'fiat' && account.currency === 'IRR',
    )
  }, [accounts])

  // Calculate prices and amounts from API data
  const instantBuyPrice = +(otcPrice?.data?.price ?? 0)
  const instantSellPrice = +(otcPrice?.data?.price ?? 0)
  const availableAmount =
    tab === 'sell'
      ? otcPrice?.data?.targetAvailableBalance
      : otcPrice?.data?.targetMaxAmount

  const maxIRRAmount = +(
    otcPrice?.data?.baseMaxAmount ??
    accountAvailableBalance?.balance ??
    0
  )
  const maxTokenAmount =
    +(otcPrice?.data?.targetMaxAmount ?? 0) / (otcPrice?.data?.targetScale || 1)

  // Map orderData to flat structure (use convert order data for convert tab, otherwise use regular order data)
  const flatOrderData = useMemo(() => {
    if (tab === 'convert' && otcPlaceConvertOrderData) {
      return mapOrderDataToFlat(otcPlaceConvertOrderData)
    }
    return mapOrderDataToFlat(otcPlaceOrderData)
  }, [tab, otcPlaceOrderData, otcPlaceConvertOrderData])

  const referralCode = undefined
  const referal = otcPlaceOrderData?.data.order?.referral
  const affiliateCode = Cookie.get('affiliated_code')

  return (
    <Col className='gap-md w-full md:w-96   mb-8xl md:mb-md '>
      {project?.tradeStatus === 'active' ? (
        <OrderBox
          mode='easy'
          tab={tab}
          setTab={setTab}
          selectedAsset={selectedAsset}
          onAssetChange={setSelectedAsset}
          availableBalance={maxIRRAmount}
          availableAmount={availableAmount}
          isBalanceLoading={isPortfolioLoading}
          instantBuyPrice={instantBuyPrice}
          instantSellPrice={instantSellPrice}
          isInstantPriceLoading={isOtcPriceLoading || isFetching}
          inputCurrency='IRR'
          outputCurrency='TMN'
          hideAssetSelect
          hideModeSwitch
          maxIRRAmount={maxIRRAmount}
          maxTokenAmount={maxTokenAmount}
          targetScale={otcPrice?.data?.targetScale}
          onPlaceOrder={otcPlaceOrder}
          onPayOrder={otcPayOrder}
          flatOrderData={flatOrderData}
          isPlacingOrder={
            tab === 'convert'
              ? isOtcPlaceConvertOrderLoading
              : isOtcPlaceOrderLoading
          }
          isPayingOrder={
            tab === 'convert'
              ? isOtcFinalizeConvertOrderLoading
              : isOtcPayOrderLoading
          }
          userId={user?.data?.uuid}
          allProjects={
            projects?.map((project) => mapProjectToFlatAsset(project)) || []
          }
          availableAssets={userAssets}
          onResetOrderData={handleResetOtcData}
          listProjectsData={listProjectsData}
          onSearchChange={setSearchQuery}
          defaultReferralCode={referralCode}
          onCheckReferralCode={
            tab === 'convert' ? handleCheckReferralCode : undefined
          }
          baseAvailableBalance={otcPrice?.data?.baseAvailableBalance}
          fromAsset={fromAsset}
          toAsset={toAsset}
          onFromAssetChange={setFromAsset}
          onToAssetChange={setToAsset}
          onFromAmountChange={handleFromAmountChange}
          onCalculateReceivedAmount={handleCalculateReceivedAmount}
          onCalculateReceivedAmountForToAmount={
            handleCalculateReceivedAmountForToAmount
          }
          conversionRate={conversionRate}
          isConversionRateLoading={isConversionRateLoading}
          onPlaceOrderSuccess={setConvertOrderId}
          convertOrderId={convertOrderId}
          convertOrderBaseAmount={convertOrderBaseAmount}
          convertOrderTargetAmount={convertOrderTargetAmount}
          onConvertSubmit={tab === 'convert' ? handleConvertSubmit : undefined}
          onRefreshConvertPrice={
            tab === 'convert' ? handleRefreshConvertPrice : undefined
          }
          onConvertFinalize={
            tab === 'convert' ? handleConvertFinalize : undefined
          }
          setToAmount={(amount) => setToAmount(Number(amount))}
          toAmount={toAmount}
          setFromAmount={(amount) => setFromAmount(Number(amount))}
          fromAmount={fromAmount}
          isCalculatingConvertAmount={isCalculatingConvertAmount}
          isMobile
          affiliateCode={affiliateCode}
          referal={referal}
        />
      ) : (
        <ProjectsDetailOrderBoxEmptyState status={project?.tradeStatus} />
      )}
      {/* <Orderbook /> */}
    </Col>
  )
}
